import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Check, ShieldCheck, Zap, Wallet, FileSearch, Handshake, Home, ClipboardList,
  Building2, Trees, Store, Landmark, MapPin, Layers,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { LeadForm } from "@/components/site/LeadForm";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import hero from "@/assets/hero-dark.webp";
import heroSrcSet from "@/assets/hero-dark.webp?w=640;960;1600&format=webp&as=srcset";
import advantages from "@/assets/advantages.webp";
import advantagesSrcSet from "@/assets/advantages.webp?w=480;768;1200&format=webp&as=srcset";



export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "InvestCapital — Compramos o seu imóvel a pronto pagamento" },
      { name: "description", content: "Compramos imóveis a pronto pagamento em Portugal continental: heranças, apartamentos, moradias, prédios, lojas e terrenos. Proposta em 48h úteis." },
      { property: "og:title", content: "InvestCapital — Compramos o seu imóvel a pronto pagamento" },
      { property: "og:description", content: "Proposta em 48h. Compra a pronto pagamento com capitais próprios. Sem burocracias, sem intermediários, sem comissões." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:url", content: "https://www.investcapital.pt/" },
    ],
    links: [
      { rel: "canonical", href: "https://www.investcapital.pt/" },
      {
        rel: "preload",
        as: "image",
        href: hero,
        imageSrcSet: heroSrcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),

});

const advantagesList = [
  "Proposta em 48h úteis",
  "Compra a pronto pagamento com capitais próprios",
  "Sem burocracias, sem intermediários, sem comissões",
  "Avaliação totalmente gratuita",
  "Total transparência e confidencialidade",
  "Compramos em qualquer estado de conservação",
  "Sem qualquer compromisso",
];

const propertyTypes = [
  { icon: Landmark, label: "Heranças" },
  { icon: Building2, label: "Apartamentos" },
  { icon: Home, label: "Moradias" },
  { icon: Layers, label: "Prédios" },
  { icon: Store, label: "Lojas" },
  { icon: Trees, label: "Terrenos" },
  { icon: MapPin, label: "Lotes" },
];

const steps = [
  { icon: FileSearch, title: "Identificação do imóvel", body: "Preenche o formulário com os seus contactos e a informação do imóvel. Em 48h úteis entramos em contacto para agendar visita, se necessário." },
  { icon: ClipboardList, title: "Apresentação de proposta", body: "Elaboramos uma proposta de compra a pronto pagamento com base na informação enviada, avaliação do imóvel e condições de mercado." },
  { icon: Handshake, title: "Compra a pronto pagamento", body: "Se aceitar, tratamos de toda a documentação e formalizamos a escritura em notário com pagamento imediato." },
];

function Index() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>



      {/* HERO */}
      <section className="relative overflow-hidden bg-neutral-950">
        <div className="absolute inset-0">
          <img src={hero} srcSet={heroSrcSet} sizes="100vw" alt="" className="h-full w-full object-cover opacity-70" width={1600} height={900} fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 py-24 text-center md:py-32">
          <h1 className="font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white md:text-6xl">
            Tem urgência em vender a sua casa?
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/85 md:text-lg">
            <strong className="font-semibold uppercase text-white">Compramos em 48h</strong>, sem burocracias, intermediários e sem comissões!
          </p>
          <div className="mt-8 flex justify-center">
            <a
              href="#vender-imovel"
              className="inline-flex items-center gap-2 rounded-full bg-[#22c55e] px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition-transform hover:scale-105 hover:bg-[#16a34a] md:text-base"
            >
              Vender imóvel
              <ArrowUpRight className="h-5 w-5" strokeWidth={2.5} />
            </a>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-base font-semibold uppercase text-white md:text-lg">
            <span className="flex items-center gap-2"><Zap className="h-5 w-5 text-[#22c55e]" /> Proposta em 48h</span>
            <span className="flex items-center gap-2"><Wallet className="h-5 w-5 text-[#22c55e]" /> Pronto pagamento</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#22c55e]" /> 0% comissões</span>
          </div>
        </div>
      </section>


      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="bg-muted/40 py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Como funciona</p>
            <h2 className="mt-2 font-display text-3xl font-semibold md:text-4xl">
              Da proposta à escritura em 3 passos
            </h2>
            <p className="mt-3 text-muted-foreground">Processo rápido, simplificado e seguro.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-border bg-card p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 text-xs font-semibold text-muted-foreground">Passo 0{i + 1}</div>
                <h3 className="mt-1 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild size="lg">
              <a href="#vender-imovel">Pedir proposta</a>
            </Button>
          </div>
        </div>
      </section>

      {/* TIPOS DE IMÓVEL */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">O que compramos</p>
          <h2 className="mt-2 font-display text-3xl font-semibold md:text-4xl">Todos os tipos de imóvel</h2>
          <p className="mt-3 text-muted-foreground">
            Compramos em qualquer estado de conservação, em Portugal continental.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-7">
          {propertyTypes.map((t) => (
            <div key={t.label} className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card px-4 py-5 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <t.icon className="h-5 w-5" />
              </div>
              <span className="text-sm font-medium text-foreground">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* VANTAGENS */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Vantagens</p>
          <h2 className="mt-2 font-display text-3xl font-semibold md:text-4xl">Porquê a InvestCapital?</h2>
          <p className="mt-3 text-muted-foreground">Um processo simples, transparente e rápido — do primeiro contacto à escritura.</p>
        </div>
        <div className="mt-12 grid gap-10 md:grid-cols-2 md:items-center">
          <ul className="space-y-3">
            {advantagesList.map((a) => (
              <li key={a} className="flex items-start gap-3 rounded-lg border border-border/60 bg-card p-4">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm text-foreground">{a}</span>
              </li>
            ))}
          </ul>
          <div className="overflow-hidden rounded-2xl border border-border">
            <img src={advantages} srcSet={advantagesSrcSet} sizes="(max-width: 768px) 100vw, 50vw" alt="Interior claro e acolhedor" className="h-full w-full object-cover" width={1200} height={900} loading="lazy" decoding="async" />
          </div>
        </div>
      </section>


      {/* CONDIÇÕES */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Condições</p>
          <h2 className="mt-2 font-display text-3xl font-semibold md:text-4xl">Requisitos simples</h2>
          <p className="mt-3 text-muted-foreground">
            Para avançarmos com a proposta, basta reunir:
          </p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {[
            "Ser proprietário do imóvel (aceitamos heranças e imóveis com encargos)",
            "Caderneta predial do imóvel",
            "Imóvel situado em Portugal continental",
          ].map((c) => (
            <div key={c} className="rounded-2xl border border-border bg-card p-6">
              <Check className="h-5 w-5 text-primary" />
              <p className="mt-3 text-sm text-foreground">{c}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FORM */}
      <section id="vender-imovel" className="scroll-mt-24 bg-primary-soft/60 py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-semibold md:text-4xl">Peça a sua proposta</h2>
            <p className="mt-3 text-muted-foreground">
              Somos uma empresa que representa um grupo de investidores com
              capitais próprios. A compra é sempre a pronto pagamento — sem
              burocracias, sem intermediários e sem comissões.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Proposta em 48h úteis</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Avaliação gratuita e confidencial</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Sem custos e sem compromisso</li>
            </ul>
            <p className="mt-6 text-sm">
              Prefere falar connosco?{" "}
              <Link to="/contactos" className="font-medium text-primary hover:underline">
                Ver contactos
              </Link>
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <LeadForm />
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
      <FloatingCTA />
    </div>
  );
}
